import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Switch } from "./ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter, DialogClose
} from "./ui/dialog";
import { Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { TagSelector } from "./TagSelector";

export function EditPrayerDialog({
  open,
  onOpenChange,
  prayerForm,
  setPrayerForm,
  onSubmit,
  formLoading,
  testIdPrefix = ""
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif">Edit Prayer</DialogTitle>
          <DialogDescription>Update prayer request details</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor={`${testIdPrefix}editRequest`}>Prayer Request *</Label>
            <Textarea
              id={`${testIdPrefix}editRequest`}
              value={prayerForm.request}
              onChange={(e) => setPrayerForm({ ...prayerForm, request: e.target.value })}
              required
              className="min-h-24"
              data-testid={`${testIdPrefix}edit-prayer-request`}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`${testIdPrefix}editRequestedBy`}>Requested By</Label>
              <Input
                id={`${testIdPrefix}editRequestedBy`}
                placeholder="Who is this prayer for?"
                value={prayerForm.requested_by}
                onChange={(e) => setPrayerForm({ ...prayerForm, requested_by: e.target.value })}
                data-testid={`${testIdPrefix}edit-requested-by`}
              />
            </div>
            <div className="space-y-2">
              <Label>Date Requested</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid={`${testIdPrefix}edit-date-requested`}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {prayerForm.date_requested
                      ? format(new Date(prayerForm.date_requested), "PPP")
                      : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={prayerForm.date_requested ? new Date(prayerForm.date_requested) : undefined}
                    onSelect={(date) => setPrayerForm({ ...prayerForm, date_requested: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${testIdPrefix}editNotes`}>Notes</Label>
            <Textarea
              id={`${testIdPrefix}editNotes`}
              placeholder="Any additional details..."
              value={prayerForm.notes}
              onChange={(e) => setPrayerForm({ ...prayerForm, notes: e.target.value })}
              className="min-h-16"
              data-testid={`${testIdPrefix}edit-notes`}
            />
          </div>

          <TagSelector
            selectedTags={prayerForm.tags || []}
            onChange={(tags) => setPrayerForm({ ...prayerForm, tags })}
            testIdPrefix={`${testIdPrefix}edit-`}
          />

          <div className="flex items-center space-x-2">
            <Switch
              id={`${testIdPrefix}editHidden`}
              checked={prayerForm.is_hidden}
              onCheckedChange={(checked) => setPrayerForm({ ...prayerForm, is_hidden: checked })}
              data-testid={`${testIdPrefix}edit-hidden-toggle`}
            />
            <Label htmlFor={`${testIdPrefix}editHidden`}>Hide this prayer</Label>
          </div>

          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-stone-dark mb-3">Prayer Result</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`${testIdPrefix}editResult`}>Result / Answer</Label>
                <Textarea
                  id={`${testIdPrefix}editResult`}
                  placeholder="How was this prayer answered?"
                  value={prayerForm.result}
                  onChange={(e) => setPrayerForm({ ...prayerForm, result: e.target.value })}
                  className="min-h-16"
                  data-testid={`${testIdPrefix}edit-result`}
                />
              </div>
              <div className="space-y-2">
                <Label>Date Answered</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid={`${testIdPrefix}edit-date-answered`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {prayerForm.date_answered
                        ? format(new Date(prayerForm.date_answered), "PPP")
                        : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={prayerForm.date_answered ? new Date(prayerForm.date_answered) : undefined}
                      onSelect={(date) => setPrayerForm({ ...prayerForm, date_answered: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" className="rounded-full">Cancel</Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-sage hover:bg-sage-dark text-white rounded-full"
              disabled={formLoading}
              data-testid={`${testIdPrefix}submit-edit-btn`}
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
